import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormData } from '../views/onboarding/types/onboarding';
import { APP_PATHS } from '../routes/paths';
import apiService from '../services/api.service';

export const useOnboardingForm = (email: string, onboardingId?: string) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    email: email,
    firstName: '',
    topics: '',
    topicsCustom: '',
    accounts: '',
    accountsCustom: '',
    useCase: null,
    businessName: '',
    personalGoals: '',
    businessGoals: '',
    digestFrequency: null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // NOTE: Keep this console.log for alpha testing/debugging - DO NOT REMOVE
      console.log('Form submitted:', formData);

      if (onboardingId) {
        // Update existing record with form data (and potentially updated email)
        await apiService.updateOnboarding(onboardingId, {
          email: formData.email,
          formData: formData as unknown as Record<string, unknown>,
        });
      } else {
        // Fallback: create new record if no onboardingId (initial API call failed)
        await apiService.createOnboarding({
          email: formData.email,
          formData: formData as unknown as Record<string, unknown>,
        });
      }

      navigate(APP_PATHS.thankYou);
    } catch (error) {
      console.error('Failed to submit onboarding:', error);
      // Still navigate to thank you page even if API fails
      // This ensures a good user experience during early access
      navigate(APP_PATHS.thankYou);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGoalsLabel = (): string => {
    if (formData.useCase === 'myself') return ' personal';
    if (formData.useCase === 'business') return ' business';
    return '';
  };

  const showPersonalGoals = formData.useCase === 'myself' || formData.useCase === 'both';
  const showBusinessGoals = formData.useCase === 'business' || formData.useCase === 'both';
  const showBusinessName = formData.useCase === 'business' || formData.useCase === 'both';

  return {
    formData,
    setFormData,
    handleSubmit,
    getGoalsLabel,
    showPersonalGoals,
    showBusinessGoals,
    showBusinessName,
    isSubmitting,
  };
};
